// Keycloak sign-in: authorization code + PKCE with a public client (docs/auth/keycloak-oauth2-design.md §8.3).
// Tokens live in memory only; a page reload signs in again silently through the Keycloak session cookie.
import { InMemoryWebStorage, WebStorageStateStore } from 'oidc-client-ts';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider as OidcProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { setAccessToken } from '@/api/client';
import { config } from '@/config';
import { rememberReturnTo } from './returnTo';

const oidcConfig = {
  authority: config.oidcAuthority,
  client_id: config.oidcClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  automaticSilentRenew: true,
  // The /auth/callback route (SignInCallback) moves on through the router once sign-in completes; changing
  // the address here behind the router's back would leave it showing the empty callback route.
  onSigninCallback: () => undefined,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  if (config.authDisabled) return <>{children}</>;
  return (
    <OidcProvider {...oidcConfig}>
      <RequireSignIn>{children}</RequireSignIn>
    </OidcProvider>
  );
}

/** Signing in is required: there is no anonymous use of Xpedition2. */
function RequireSignIn({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { t } = useTranslation();

  // After a spell of inactivity the Keycloak session expires, so the background token refresh fails (Keycloak
  // refuses it without CORS headers, so the browser reports "Failed to fetch"). That isn't a sign-in failure:
  // go back through Keycloak - silently if its session is still alive - and return to the same page.
  const sessionExpired = auth.error?.source === 'renewSilent' || auth.error?.source === 'signinSilent';

  useEffect(() => {
    const needsSignIn = !auth.isAuthenticated && !auth.error;
    if (!hasAuthParams() && (needsSignIn || sessionExpired) && !auth.activeNavigator && !auth.isLoading) {
      rememberReturnTo(window.location.pathname + window.location.search);
      void auth.signinRedirect();
    }
  }, [auth, sessionExpired]);

  // A token that ran out while the tab was asleep (no refresh was attempted): sign in again the same way.
  useEffect(
    () =>
      auth.events.addAccessTokenExpired(() => {
        rememberReturnTo(window.location.pathname + window.location.search);
        void auth.signinRedirect();
      }),
    [auth],
  );

  // Hand each new or renewed access token to the API client during render, not in an effect: children's
  // effects (their first API calls, e.g. GET /me for the avatar) run before this component's effects would.
  setAccessToken(auth.user?.access_token);

  if (auth.error && !sessionExpired) {
    return (
      <div role="alert" style={{ padding: 'var(--space-6)' }}>
        <p>{t('auth.error', { message: auth.error.message })}</p>
        <button
          type="button"
          onClick={() => {
            rememberReturnTo(window.location.pathname + window.location.search);
            void auth.signinRedirect();
          }}
        >
          {t('auth.retry')}
        </button>
      </div>
    );
  }
  if (!auth.isAuthenticated || sessionExpired)
    return <p style={{ padding: 'var(--space-6)' }}>{t('auth.signingIn')}</p>;
  return <>{children}</>;
}
